import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/api/client';
import { Users, MapPin, Trash2, Plus, CheckCircle2, AlertCircle } from 'lucide-react';

interface Ambito {
  id: number;
  tipo_ambito: 'REGION' | 'PROVINCIA' | 'DISTRITO';
  region: string;
  provincia: string | null;
  distrito: string | null;
  notas: string | null;
}

interface Coordinador {
  id: number;
  username: string;
  nombre_completo: string;
  email: string | null;
  dni: string | null;
  activo: boolean;
  ambitos: Ambito[];
}

interface Jerarquia {
  jerarquia: Record<string, Record<string, string[]>>;
}

const TIPO_LABEL: Record<string, string> = {
  REGION:    '🗺️ Regional',
  PROVINCIA: '🏛️ Provincial',
  DISTRITO:  '📍 Distrital',
};

const TIPO_CLS: Record<string, string> = {
  REGION:    'bg-purple-100 text-purple-800',
  PROVINCIA: 'bg-blue-100 text-blue-800',
  DISTRITO:  'bg-emerald-100 text-emerald-800',
};

function AsignarAmbitoForm({ userId, onSaved }: { userId: number; onSaved: () => void }) {
  const qc = useQueryClient();
  const [tipo, setTipo] = useState<'REGION' | 'PROVINCIA' | 'DISTRITO'>('REGION');
  const [region, setRegion] = useState('');
  const [provincia, setProvincia] = useState('');
  const [distrito, setDistrito] = useState('');
  const [notas, setNotas] = useState('');
  const [error, setError] = useState<string | null>(null);

  const { data: cat } = useQuery<Jerarquia>({
    queryKey: ['catalogo-ambitos'],
    queryFn: async () => (await api.get('/coordinadores/catalogo-ambitos')).data,
    staleTime: 10 * 60 * 1000,
  });

  const regiones = cat ? Object.keys(cat.jerarquia).sort() : [];
  const provincias = cat && region ? Object.keys(cat.jerarquia[region] ?? {}).sort() : [];
  const distritos = cat && region && provincia ? (cat.jerarquia[region]?.[provincia] ?? []) : [];

  const mut = useMutation({
    mutationFn: async () => {
      const payload: any = { tipo_ambito: tipo, region, notas: notas || null };
      if (tipo !== 'REGION') payload.provincia = provincia;
      if (tipo === 'DISTRITO') payload.distrito = distrito;
      return (await api.post(`/coordinadores/${userId}/ambitos`, payload)).data;
    },
    onSuccess: () => {
      setRegion(''); setProvincia(''); setDistrito(''); setNotas(''); setError(null);
      qc.invalidateQueries({ queryKey: ['coordinadores'] });
      onSaved();
    },
    onError: (e: any) => setError(e?.response?.data?.detail ?? 'Error al asignar'),
  });

  return (
    <div className="bg-blue-50 border border-blue-200 rounded p-3 space-y-2">
      <div className="text-xs font-semibold text-blue-900">+ Asignar ámbito de coordinación</div>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-2 text-xs">
        <div>
          <label className="block text-gray-600">Tipo</label>
          <select
            value={tipo}
            onChange={(e) => { setTipo(e.target.value as any); setProvincia(''); setDistrito(''); }}
            className="w-full border rounded px-2 py-1"
          >
            <option value="REGION">🗺️ Regional</option>
            <option value="PROVINCIA">🏛️ Provincial</option>
            <option value="DISTRITO">📍 Distrital</option>
          </select>
        </div>
        <div>
          <label className="block text-gray-600">Región</label>
          <select
            value={region}
            onChange={(e) => { setRegion(e.target.value); setProvincia(''); setDistrito(''); }}
            className="w-full border rounded px-2 py-1"
          >
            <option value="">— Selecciona —</option>
            {regiones.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-gray-600">Provincia</label>
          <select
            value={provincia}
            onChange={(e) => { setProvincia(e.target.value); setDistrito(''); }}
            disabled={tipo === 'REGION' || !region}
            className="w-full border rounded px-2 py-1 disabled:bg-gray-100"
          >
            <option value="">— Selecciona —</option>
            {provincias.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-gray-600">Distrito</label>
          <select
            value={distrito}
            onChange={(e) => setDistrito(e.target.value)}
            disabled={tipo !== 'DISTRITO' || !provincia}
            className="w-full border rounded px-2 py-1 disabled:bg-gray-100"
          >
            <option value="">— Selecciona —</option>
            {distritos.map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
        </div>
      </div>
      <div>
        <label className="block text-gray-600 text-xs">Notas (opcional)</label>
        <input
          value={notas}
          onChange={(e) => setNotas(e.target.value)}
          className="w-full border rounded px-2 py-1 text-xs"
          placeholder="Observación interna"
        />
      </div>
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-xs rounded p-2 flex items-start gap-2">
          <AlertCircle size={14} className="mt-0.5 shrink-0" />
          <span>{error}</span>
        </div>
      )}
      <button
        onClick={() => mut.mutate()}
        disabled={
          mut.isPending || !region ||
          (tipo !== 'REGION' && !provincia) ||
          (tipo === 'DISTRITO' && !distrito)
        }
        className="bg-arpp-primary text-white text-xs px-4 py-1.5 rounded disabled:opacity-50"
      >
        {mut.isPending ? 'Asignando…' : 'Asignar ámbito'}
      </button>
    </div>
  );
}

function CoordinadorCard({ cor }: { cor: Coordinador }) {
  const qc = useQueryClient();
  const [showForm, setShowForm] = useState(false);

  const delMut = useMutation({
    mutationFn: async (ambitoId: number) =>
      (await api.delete(`/coordinadores/ambitos/${ambitoId}`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['coordinadores'] }),
  });

  return (
    <div className="bg-white border rounded p-4 space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="font-semibold text-arpp-primary">{cor.nombre_completo}</div>
          <div className="text-xs text-gray-600 font-mono">
            usr: {cor.username}{cor.dni ? ` · DNI ${cor.dni}` : ''}
          </div>
          {cor.email && <div className="text-xs text-gray-500">{cor.email}</div>}
        </div>
        <button
          onClick={() => setShowForm((v) => !v)}
          className="text-xs bg-arpp-primary text-white px-3 py-1 rounded inline-flex items-center gap-1"
        >
          <Plus size={12} /> Ámbito
        </button>
      </div>

      {showForm && (
        <AsignarAmbitoForm userId={cor.id} onSaved={() => setShowForm(false)} />
      )}

      <div>
        <div className="text-[11px] uppercase text-gray-500 mb-1">
          Ámbitos asignados ({cor.ambitos.length})
        </div>
        {cor.ambitos.length === 0 ? (
          <div className="text-xs text-gray-500 italic">
            Sin ámbitos asignados — el coordinador no puede ver candidatos hasta que se le asigne uno.
          </div>
        ) : (
          <div className="flex flex-wrap gap-1.5">
            {cor.ambitos.map((amb) => (
              <div
                key={amb.id}
                className={`text-[11px] px-2 py-1 rounded inline-flex items-center gap-2 ${TIPO_CLS[amb.tipo_ambito]}`}
              >
                <MapPin size={11} />
                <span className="font-medium">{TIPO_LABEL[amb.tipo_ambito]}</span>
                <span>·</span>
                <span>
                  {amb.region}
                  {amb.provincia && ` / ${amb.provincia}`}
                  {amb.distrito && ` / ${amb.distrito}`}
                </span>
                <button
                  onClick={() => { if (confirm('¿Quitar este ámbito?')) delMut.mutate(amb.id); }}
                  className="text-red-700 hover:bg-white rounded p-0.5 ml-1"
                  title="Quitar ámbito"
                >
                  <Trash2 size={11} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export function Coordinadores() {
  const { data, isLoading } = useQuery<{ total: number; items: Coordinador[] }>({
    queryKey: ['coordinadores'],
    queryFn: async () => (await api.get('/coordinadores')).data,
  });

  return (
    <div className="p-6 space-y-4 max-w-5xl">
      <header>
        <h1 className="text-xl font-bold text-arpp-primary inline-flex items-center gap-2">
          <Users size={22} /> Coordinadores
        </h1>
        <p className="text-xs text-gray-600">
          {data?.total ?? '…'} coordinadores (rol COR). Cada uno tiene uno o más ámbitos territoriales asignados.
        </p>
        <div className="bg-blue-50 border border-blue-200 rounded p-3 text-xs text-blue-900 mt-2 space-y-1">
          <div className="font-semibold">Jerarquía de coordinación</div>
          <div>• <strong>Coordinador General</strong> (ADM/PLT/PLA) + <strong>Asistentes</strong> (ASI) → ven TODO sin restricción.</div>
          <div>• <strong>Coordinador Regional</strong> → ve su región + todas sus provincias y distritos.</div>
          <div>• <strong>Coordinador Provincial</strong> → ve su provincia + distritos comprendidos.</div>
          <div>• <strong>Coordinador Distrital</strong> → solo su distrito.</div>
          <div className="italic text-blue-800">Lima Metropolitana: no tiene COR regional; cada distrito tiene su propio coordinador distrital. Una misma persona puede ocupar varios cargos simultáneos.</div>
        </div>
      </header>

      {isLoading && <div className="text-sm text-gray-500">Cargando…</div>}

      {data && data.items.length === 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded p-4 text-sm text-amber-900 inline-flex items-center gap-2">
          <CheckCircle2 size={16} />
          No hay usuarios con rol COR todavía. Crea usuarios desde el panel de administración con rol = COR para empezar a asignar ámbitos.
        </div>
      )}

      {data?.items.map((cor) => <CoordinadorCard key={cor.id} cor={cor} />)}
    </div>
  );
}
